import { useRef } from 'react';
import { Button } from '../ui';
import { acceptFiles, MAX_FILES, type Attachment } from './attachments';

/**
 * Shared attach UI for both AI screens. Split in two because the chip row sits above the composer
 * while the 📎 button sits inside the composer row — same state, two placements, no duplicated logic.
 */

interface BarProps {
  files: Attachment[];
  setFiles: React.Dispatch<React.SetStateAction<Attachment[]>>;
  onError: (msg: string) => void;
  disabled?: boolean;
}

/** The 📎 picker button + its hidden file input. */
export function AttachButton({ files, setFiles, onError, disabled }: BarProps) {
  const ref = useRef<HTMLInputElement | null>(null);
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files ?? []);
    e.target.value = ''; // allow re-picking the same file
    const { accepted, error } = await acceptFiles(files, picked);
    if (error) onError(error);
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  }
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept="image/*,application/pdf"
        multiple
        style={{ display: 'none' }}
        onChange={onPick}
      />
      <Button
        type="button"
        variant="secondary"
        onClick={() => ref.current?.click()}
        disabled={disabled || files.length >= MAX_FILES}
        aria-label="پیوست تصویر یا PDF"
      >
        📎
      </Button>
    </>
  );
}

/** Removable chips for the currently attached files. Renders nothing when empty. */
export function AttachChips({ files, setFiles }: Pick<BarProps, 'files' | 'setFiles'>) {
  if (!files.length) return null;
  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {files.map((f, i) => (
        <span
          key={i}
          className="chip"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          {f.kind === 'image' ? '🖼' : '📎'} {f.name}
          <button
            type="button"
            onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'inherit' }}
            aria-label="حذف پیوست"
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );
}
